import React, { useState, useEffect } from 'react';
import { 
  DocumentTextIcon, 
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import api from '../api/client';

export default function WorkflowList() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      const data = await api.listWorkflows();
      setWorkflows(data.workflows || getDemoWorkflows());
    } catch (error) {
      console.error('Failed to load workflows:', error);
      setWorkflows(getDemoWorkflows());
    } finally {
      setLoading(false);
    }
  };

  const getDemoWorkflows = () => [
    {
      workflow_id: 'wf_20251231_221921_f7440d',
      workflow_type: 'security_assessment',
      status: 'completed',
      priority: 'high',
      created_at: '2025-12-31T22:19:21Z',
      completed_at: '2025-12-31T22:19:28Z',
      total_steps: 6,
      completed_steps: 6,
    },
    {
      workflow_id: 'wf_20251231_220624_4c7771',
      workflow_type: 'security_assessment',
      status: 'completed',
      priority: 'high',
      created_at: '2025-12-31T22:06:24Z',
      completed_at: '2025-12-31T22:06:30Z',
      total_steps: 6,
      completed_steps: 6,
    },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-400" />;
      case 'running':
        return <ArrowPathIcon className="h-5 w-5 text-primary-400 animate-spin" />;
      case 'failed':
        return <ExclamationCircleIcon className="h-5 w-5 text-danger-400" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-900 text-green-300 border-green-700',
      running: 'bg-primary-900 text-primary-300 border-primary-700',
      failed: 'bg-danger-900 text-danger-300 border-danger-700',
      pending: 'bg-gray-700 text-gray-300 border-gray-600',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const formatWorkflowType = (type) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const calculateDuration = (start, end) => {
    if (!end) return 'In progress...';
    const startTime = new Date(start);
    const endTime = new Date(end);
    const seconds = Math.floor((endTime - startTime) / 1000);
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Security Assessments</h2>
          <p className="text-gray-400 mt-1">View your workflow history and results</p>
        </div>
        <button
          onClick={loadWorkflows}
          className="flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <ArrowPathIcon className="h-5 w-5" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Workflow List */}
      <div className="space-y-4">
        {workflows.length === 0 ? (
          <div className="bg-gray-800 rounded-lg p-12 text-center border border-gray-700">
            <DocumentTextIcon className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-400">No assessments yet</h3>
            <p className="text-sm text-gray-500 mt-2">
              Run your first security assessment in the AI Assistant tab
            </p>
          </div>
        ) : (
          workflows.map((workflow) => (
            <div
              key={workflow.workflow_id}
              className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-gray-600 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  {/* Icon */}
                  <div className="bg-primary-900/50 p-3 rounded-lg">
                    {getStatusIcon(workflow.status)}
                  </div>

                  {/* Details */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">
                        {formatWorkflowType(workflow.workflow_type)}
                      </h3>
                      {getStatusBadge(workflow.status)}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-gray-400">Workflow ID</p>
                        <p className="text-sm text-gray-300 font-mono mt-1">
                          {workflow.workflow_id.substring(0, 20)}...
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Started</p>
                        <p className="text-sm text-gray-300 mt-1">
                          {formatDate(workflow.created_at)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Duration</p>
                        <p className="text-sm text-gray-300 mt-1">
                          {calculateDuration(workflow.created_at, workflow.completed_at)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Progress</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="flex-1 bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-primary-500 h-2 rounded-full transition-all"
                              style={{
                                width: `${(workflow.completed_steps / workflow.total_steps) * 100}%`,
                              }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-300">
                            {workflow.completed_steps}/{workflow.total_steps}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Priority Badge */}
                {workflow.priority && (
                  <div className="ml-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        workflow.priority === 'urgent'
                          ? 'bg-danger-900 text-danger-300'
                          : workflow.priority === 'high'
                          ? 'bg-orange-900 text-orange-300'
                          : 'bg-gray-700 text-gray-300'
                      }`}
                    >
                      {workflow.priority.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
